import React from "react";

import { gql } from "@apollo/client";
import { type ProviderWrapperProps } from "@components/utilities";
import { useNorthbeamQuery } from "@hooks/http";
import { type GetSalesBreakdownConfigs } from "@north-beam/nb-client-side";
import { type CategoricalBreakdownConfig } from "@north-beam/nb-common";

export const defaultSalesBreakdownConfigs: CategoricalBreakdownConfig[] = [
  {
    key: "Platform (Northbeam)",
    name: "Platform (Northbeam)",
    choices: [],
    type: "categorical",
    default: true,
  },
];

export interface SalesBreakdownConfigsContextType {
  value: CategoricalBreakdownConfig[];
  refresh: () => Promise<void>;
  loading: boolean;
}

const SalesBreakdownConfigsContext =
  React.createContext<SalesBreakdownConfigsContextType>({
    value: defaultSalesBreakdownConfigs,
    refresh: () => Promise.resolve(),
    loading: false,
  });

export const useSalesBreakdownConfigs = () =>
  React.useContext(SalesBreakdownConfigsContext);

export function SalesBreakdownConfigsProvider(props: ProviderWrapperProps) {
  const { data, refetch, loading } =
    useNorthbeamQuery<GetSalesBreakdownConfigs>(SALES_BREAKDOWN_QUERY, {
      pollInterval: 600_000,
      doNotRaiseErrorOnFailure: true,
    });

  const value = data?.me?.salesBreakdownConfigs;

  const breakdownConfigs: CategoricalBreakdownConfig[] = React.useMemo(() => {
    if (!value) {
      return defaultSalesBreakdownConfigs;
    }
    return value.map((v) => ({ type: "categorical", ...v }));
  }, [value]);

  const refresh = React.useCallback(async () => {
    await refetch();
  }, [refetch]);

  return (
    <SalesBreakdownConfigsContext.Provider
      {...props}
      value={{ value: breakdownConfigs, refresh, loading }}
    />
  );
}

export const SALES_BREAKDOWN_QUERY = gql`
  query GetSalesBreakdownConfigs {
    me {
      id
      salesBreakdownConfigs {
        key
        name
        choices {
          value
          label
        }
      }
    }
  }
`;
